import React, { useRef } from "react";
import { Star } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";

// Real reviews scraped from HomeStars, 123Fixit, and meinhaus.ca
const REVIEWS = [
  // === HomeStars (verified) ===
  {
    quote:
      "Great crew, very professional, efficient and so polite. Even though we could not be on site, they kept us updated with pictures and videos throughout the entire process. Josh took the stress of the demo off of us!",
    name: "Homeowner",
    location: "Demolition · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "Josh and Erik did a great job repairing our rotting fascia and drooping gutter. The work was done quickly and of high quality. They are both very professional and I highly recommend them.",
    name: "Homeowner",
    location: "Soffit & Fascia · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "Great crew, did an awesome job from start to finish. Would definitely recommend them for their professional work.",
    name: "Bruno",
    location: "Driveway Paving · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "Outline of project was also very clear from the start, and Erik managed all the different organization needed to get the project up to code, and done the way we envisioned. I would absolutely recommend MeinHaus to anyone looking to finish any home project.",
    name: "Jordan",
    location: "Basement Reno · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "Erik was an amazing contractor. Very responsive and completed the job on time. In fact, came back a second time for touch ups as promised. Would recommend to anyone.",
    name: "Frank",
    location: "Paint Job · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "From the moment I selected him, to leading me through next steps, I always knew I made the right decision selecting him. The painter did an IMPECCABLE job! VERY impressed with the entire process from start to finish.",
    name: "Jim",
    location: "Interior Paint · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "First time using HomeStars and we hired Erik from MeinHaus. They flattened ~400 sq ft of ceilings — skim-coat plastered, primed and painted. Jeremy did an excellent job. Will definitely hire again.",
    name: "Homeowner",
    location: "Ceiling Restoration · HomeStars",
    source: "homestars",
  },
  {
    quote:
      "Several laminate planks had lifted. We thought it was a simple fix — naturally it was more serious. The guys were fantastic. Work was completed next day. Looks fabulous. Would recommend you to everyone.",
    name: "Homeowner",
    location: "Bathroom Floor · HomeStars",
    source: "homestars",
  },
  // === 123Fixit (verified) ===
  {
    quote:
      "My basement remodeling was tough — I had a leaking foundation and some other problems, but the team was efficient and did everything accurately.",
    name: "Mark",
    location: "Basement Reno · 123Fixit",
    source: "123fixit",
  },
  {
    quote:
      "The whole process went smoothly and the crew was very cooperative and friendly. Really happy to hire them.",
    name: "Olivia",
    location: "Renovation · 123Fixit",
    source: "123fixit",
  },
  {
    quote:
      "Josh and Erik are very considerate and cooperative. My home renovation work was done promptly, happy with the results.",
    name: "Frank",
    location: "Home Renovation · 123Fixit",
    source: "123fixit",
  },
  // === meinhaus.ca testimonials ===
  {
    quote:
      "Their commitment to excellence and attention to detail made the entire process smooth and stress-free.",
    name: "Nathan",
    location: "Oshawa, Ontario",
    source: "meinhaus",
  },
  {
    quote:
      "Everything went as smooth as possible during the renovations — great communication and competitive prices.",
    name: "Randall",
    location: "Homeowner",
    source: "meinhaus",
  },
  {
    quote:
      "Cared about doing the work right and brought every existing issue to our attention.",
    name: "Bridgette",
    location: "Toronto, Ontario",
    source: "meinhaus",
  },
  {
    quote:
      "Our sales consultant was so patient, knowledgeable and thorough — she made the whole home building process a pleasant experience.",
    name: "Lawson",
    location: "Toronto, Ontario",
    source: "meinhaus",
  },
  {
    quote:
      "I had tiling done in my foyer, powder room and kitchen plus a powder room reno — beautiful work, the team cleaned up daily.",
    name: "Roza",
    location: "Toronto, Ontario",
    source: "meinhaus",
  },
  {
    quote:
      "They helped us navigate the challenges encountered with our condo when trying to get the work done. The whole process was quick and the results are amazing.",
    name: "Jill",
    location: "Toronto, Ontario",
    source: "meinhaus",
  },
];

const PLATFORMS = [
  {
    label: "Google",
    href: "https://www.google.com/search?q=meinhaus+reviews",
    logo: "https://cdn.simpleicons.org/google/4285F4",
  },
  {
    label: "Houzz",
    href: "https://www.houzz.com/professionals/general-contractor",
    logo: "https://cdn.simpleicons.org/houzz/4DBC15",
  },
  {
    label: "Yelp",
    href: "https://www.yelp.com/search?find_desc=meinhaus",
    logo: "https://cdn.simpleicons.org/yelp/D32323",
  },
  {
    label: "Trustpilot",
    href: "https://www.trustpilot.com/review/meinhaus.ca",
    logo: "https://cdn.simpleicons.org/trustpilot/00B67A",
  },
  {
    label: "HomeStars",
    href: "https://homestars.com/companies/search?q=meinhaus",
    // simpleicons doesn't have HomeStars — use a custom inline SVG
    custom: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#FF6B00">
        <path d="M12 2 1 12h3v9h6v-6h4v6h6v-9h3z" />
      </svg>
    ),
  },
  {
    label: "BBB",
    href: "https://www.bbb.org/search?find_text=meinhaus",
    // simpleicons doesn't have BBB — use simple text shield
    custom: (
      <div className="flex items-center justify-center w-5 h-5 rounded bg-[#005DAA] text-white font-black text-[8px] tracking-tighter">
        BBB
      </div>
    ),
  },
];

const Stars = ({ size = 4 }) => (
  <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-${size} h-${size}`}
        fill="#F26B1F"
        stroke="#F26B1F"
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const initials = (name) =>
  name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const ReviewCard = ({ review }) => (
  <article
    className="relative bg-white rounded-2xl p-7 sm:p-8 transition-all duration-200 h-full flex flex-col"
    style={{
      border: "1.5px solid #1AA0DD",
    }}
  >
    <Stars />

    <p className="mt-4 text-[#0f172a] text-[15px] sm:text-[15.5px] leading-[1.6] flex-1">
      &ldquo;{review.quote}&rdquo;
    </p>

    <div
      className="mt-6 flex items-center gap-3 pt-5"
      style={{ borderTop: "1px solid #E2E8F0" }}
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center text-[14px] font-bold text-white shrink-0"
        style={{ backgroundColor: "#1AA0DD" }}
      >
        {initials(review.name)}
      </div>
      <div>
        <div className="font-bold text-[15px] text-[#0f172a] leading-tight">
          {review.name}
        </div>
        <div className="text-[12.5px] text-[#475569] leading-tight mt-0.5">
          {review.location}
        </div>
      </div>
    </div>
  </article>
);

const ReviewsSection = () => {
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  return (
    <section className="relative w-full pt-16 pb-20 lg:pt-24 lg:pb-28 overflow-hidden">
      {/* Background image — fixed (parallax), only visible at the bottom of the section */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_meinhaus-nav/artifacts/li4gj0j0_image.png')",
          backgroundAttachment: "fixed",
          zIndex: 0,
        }}
      />
      {/* Layered overlay: grey (top) → pure white (middle) → transparent (bottom) */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #EEF2F6 0%, rgba(255,255,255,0.92) 25%, rgba(255,255,255,0.92) 100%)",
          zIndex: 1,
        }}
      />

      <div
        className="relative max-w-[1200px] mx-auto px-4"
        style={{ zIndex: 2 }}
      >
        {/* Header */}
        <div className="text-center max-w-[760px] mx-auto mb-12 lg:mb-14">
          <span
            className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-semibold tracking-[0.18em] uppercase mb-3"
            style={{ color: "#1AA0DD" }}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1AA0DD]" />
            Real Stories From Real Homeowners
          </span>
          <h2 className="font-extrabold text-[#0f172a] tracking-tight text-[32px] sm:text-[40px] lg:text-[46px] leading-[1.08]">
            What Clients Are{" "}
            <span style={{ color: "#F26B1F" }}>Saying</span>
          </h2>
          <p className="mt-4 text-[#1e293b] text-[15px] sm:text-[16px] leading-[1.6] font-medium">
            Trusted by 5,000+ homeowners across Toronto, Oshawa, and beyond.
            Every project is backed by our{" "}
            <span className="font-bold text-[#0f172a]">
              Done-Right Guarantee
            </span>
            .
          </p>

          {/* Aggregated rating bar — orange border, sits inline with content */}
          <div
            className="mt-6 inline-flex items-center gap-2.5 bg-white rounded-full px-5 py-2.5"
            style={{ border: "2px solid #F26B1F" }}
          >
            <Stars />
            <span className="text-[14px] font-bold text-[#0f172a]">4.7/5</span>
            <span className="text-[13.5px] text-[#475569]">
              from 78+ verified reviews
            </span>
          </div>
        </div>

        {/* Reviews carousel */}
        <Carousel
          opts={{ align: "start", loop: true }}
          plugins={[autoplay.current]}
          className="w-full relative"
        >
          <CarouselContent className="-ml-5 lg:-ml-6">
            {REVIEWS.map((r, i) => (
              <CarouselItem
                key={i}
                className="pl-5 lg:pl-6 basis-full md:basis-1/2 lg:basis-1/3"
              >
                <div className="h-full pb-2 pt-2">
                  <ReviewCard review={r} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselPrevious className="hidden sm:flex -left-2 lg:-left-5 h-11 w-11 bg-white border-[1.5px] border-[#1AA0DD] text-[#1AA0DD] hover:bg-[#1AA0DD] hover:text-white transition-colors" />
          <CarouselNext className="hidden sm:flex -right-2 lg:-right-5 h-11 w-11 bg-white border-[1.5px] border-[#1AA0DD] text-[#1AA0DD] hover:bg-[#1AA0DD] hover:text-white transition-colors" />
        </Carousel>

        {/* "As reviewed on" platform strip — moved BELOW carousel for less crowding */}
        <div
          className="mt-12 lg:mt-14 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4 sm:gap-8"
          style={{ borderTop: "1px solid rgba(15,23,42,0.10)" }}
        >
          <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#475569] text-center">
            As reviewed on
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-4">
            {PLATFORMS.map((p) => (
              <a
                key={p.label}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={p.label}
                title={p.label}
                className="group inline-flex items-center gap-2 transition-all duration-200 hover:-translate-y-0.5 grayscale hover:grayscale-0 opacity-70 hover:opacity-100"
              >
                {p.custom ? (
                  p.custom
                ) : (
                  <img
                    src={p.logo}
                    alt={p.label}
                    className="h-5 w-5"
                    loading="lazy"
                  />
                )}
                <span className="text-[13px] font-semibold text-[#0f172a]">
                  {p.label}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
